import { useCallback, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import SmashLoader from '../../../components/Loader/SmashLoader';
import { getInterview } from '../../../services/Interviews/interviewService';

const tabs = [
  ['Overview', 'overview'],
  ['Questions', 'questions'],
  ['Sessions', 'sessions'],
  ['Reports', 'reports'],
  ['Settings', 'settings'],
];

const formatDeadline = (value) => value
  ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
  : 'No deadline';

export default function InterviewWorkspaceLayout() {
  const { uuid } = useParams();
  const [interview, setInterview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadInterview = useCallback((signal) => {
    setLoading(true);
    setError('');
    return getInterview(uuid, { signal })
      .then(setInterview)
      .catch((requestError) => {
        if (!signal?.aborted) setError(requestError?.response?.data?.detail || 'Unable to load this interview.');
      })
      .finally(() => {
        if (!signal?.aborted) setLoading(false);
      });
  }, [uuid]);

  useEffect(() => {
    const controller = new AbortController();
    loadInterview(controller.signal);
    return () => controller.abort();
  }, [loadInterview]);

  if (loading) return <div className="flex min-h-[60vh] items-center justify-center"><SmashLoader /></div>;
  if (error || !interview) return <div role="alert" className="mx-auto mt-12 max-w-2xl rounded-2xl border border-red-200 bg-red-50 p-6 text-red-800"><h1 className="text-xl font-semibold">Interview unavailable</h1><p className="mt-2">{error}</p><button onClick={() => loadInterview()} className="mt-4 rounded-lg bg-red-700 px-4 py-2 font-semibold text-white">Try again</button></div>;

  return (
    <div className="min-h-full bg-gray-50 dark:bg-gray-900">
      <header className="border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="mx-auto max-w-7xl px-4 pt-6 sm:px-6 lg:px-8">
          <Link to="/organization/interviews" className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-800"><ArrowLeftIcon className="h-4 w-4" />All interviews</Link>
          <div className="mt-5 flex flex-col justify-between gap-5 pb-6 lg:flex-row lg:items-end">
            <div className="min-w-0"><div className="flex flex-wrap items-center gap-2"><h1 className="truncate text-3xl font-bold text-gray-950 dark:text-white">{interview.name}</h1><span className={`rounded-full px-3 py-1 text-xs font-semibold ${interview.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}`}>{interview.is_active ? 'Active' : 'Inactive'}</span></div><p className="mt-2 max-w-3xl text-sm text-gray-600 dark:text-gray-300">{interview.description || 'No interview description has been added.'}</p></div>
            <div className="flex shrink-0 flex-wrap gap-3 text-sm"><span className="inline-flex items-center gap-2 rounded-xl bg-gray-100 px-3 py-2 dark:bg-gray-700"><CalendarDaysIcon className="h-4 w-4" />{formatDeadline(interview.deadline)}</span><span className="rounded-xl bg-gray-100 px-3 py-2 dark:bg-gray-700">{interview.questions_count} questions</span><span className="rounded-xl bg-gray-100 px-3 py-2 dark:bg-gray-700">~{interview.estimated_duration_minutes} min</span></div>
          </div>
          <nav aria-label="Interview workspace" className="flex gap-1 overflow-x-auto">
            {tabs.map(([label, path]) => <NavLink key={path} to={path} className={({ isActive }) => `whitespace-nowrap border-b-2 px-4 py-3 text-sm font-semibold ${isActive ? 'border-blue-600 text-blue-700 dark:text-blue-300' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-800 dark:text-gray-300'}`}>{label}</NavLink>)}
          </nav>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-7 sm:px-6 lg:px-8"><Outlet context={{ interview, refreshInterview: loadInterview }} /></main>
    </div>
  );
}
